import { useState, useCallback } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Link,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { resetPassword, confirmPassword } from "../../api/auth";
import { colors } from "../../theme/colors";
import AuthPage, {
  authTextFieldSx,
  authSubmitButtonSx,
} from "../auth-page/auth-page";
import PasswordRequirements, {
  passwordMeetsRequirements,
} from "../auth-page/passwordRequirements";

const notificationText = "Password successfully updated.";

export default function ResetPasswordForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error",
    duration: 2000,
  });

  const showNotification = useCallback(
    (message, severity = "error", duration = 2000) => {
      setNotification({ open: true, message, severity, duration });
    },
    []
  );

  const onSubmitEmail = useCallback(
    async (e) => {
      e.preventDefault();

      if (!email.trim()) {
        setErrors({ email: "Email is required" });
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setErrors({ email: "Email is invalid" });
        return;
      }

      setErrors({});
      setLoading(true);
      const result = await resetPassword(email);
      setLoading(false);

      if (!result.isOk) {
        showNotification(result.message, "error", 2000);
        return;
      }

      setCodeSent(true);
    },
    [email, showNotification]
  );

  const onSubmitCode = useCallback(
    async (e) => {
      e.preventDefault();

      const nextErrors = {};
      if (!code.trim()) {
        nextErrors.code = "Verification code is required";
      }
      if (!password) {
        nextErrors.password = "Password is required";
      } else if (!passwordMeetsRequirements(password)) {
        nextErrors.password = "Password does not meet the requirements.";
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      setErrors({});
      setLoading(true);
      const result = await confirmPassword(email, code, password);
      setLoading(false);

      if (result.isOk) {
        toast.success(notificationText, { duration: 2500 });
        navigate("/login");
      } else {
        const message =
          typeof result.message === "string"
            ? result.message
            : result.message?.message || "Could not update password.";
        showNotification(message, "error", 2000);
      }
    },
    [code, email, navigate, password, showNotification]
  );

  const signInLink = (
    <Box sx={{ textAlign: "center", mt: 0.5 }}>
      <Link
        component={RouterLink}
        to="/login"
        underline="hover"
        sx={{
          color: `${colors.muted} !important`,
          fontSize: "0.8rem",
          "&:hover": {
            color: `${colors.muted} !important`,
          },
        }}
      >
        Return to Sign In
      </Link>
    </Box>
  );

  return (
    <AuthPage
      subtitle={
        codeSent ? (
          <>
            If the email exists within our database, you will receive a
            verification code shortly. Please check your Spam folder.
            <br />
            <br />
            Enter the verification code from your email and set a new password.
          </>
        ) : (
          "Enter the email you used to register, and we will send a verification code."
        )
      }
      notification={notification}
      onCloseNotification={() =>
        setNotification((prev) => ({ ...prev, open: false }))
      }
    >
      {codeSent ? (
        <Box
          component="form"
          onSubmit={onSubmitCode}
          noValidate
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          <TextField
            name="code"
            placeholder="Please input verification code"
            variant="outlined"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
              if (errors.code) {
                setErrors((prev) => ({ ...prev, code: "" }));
              }
            }}
            error={!!errors.code}
            helperText={errors.code}
            disabled={loading}
            fullWidth
            autoComplete="one-time-code"
            InputLabelProps={{ shrink: false }}
            sx={authTextFieldSx}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <TextField
              name="password"
              type="password"
              placeholder="Enter new password"
              variant="outlined"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (errors.password) {
                  setErrors((prev) => ({ ...prev, password: "" }));
                }
              }}
              error={!!errors.password}
              helperText={errors.password}
              disabled={loading}
              fullWidth
              autoComplete="new-password"
              InputLabelProps={{ shrink: false }}
              sx={authTextFieldSx}
            />
            <PasswordRequirements password={password} />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            sx={authSubmitButtonSx}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "inherit" }} />
            ) : (
              "Change my password"
            )}
          </Button>
          {signInLink}
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={onSubmitEmail}
          noValidate
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          <TextField
            name="email"
            type="email"
            placeholder="Email"
            variant="outlined"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (errors.email) {
                setErrors((prev) => ({ ...prev, email: "" }));
              }
            }}
            error={!!errors.email}
            helperText={errors.email}
            disabled={loading}
            fullWidth
            autoComplete="email"
            InputLabelProps={{ shrink: false }}
            sx={authTextFieldSx}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            sx={authSubmitButtonSx}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "inherit" }} />
            ) : (
              "Reset my password"
            )}
          </Button>
          {signInLink}
        </Box>
      )}
    </AuthPage>
  );
}
